import React, { useEffect, useState, useRef, useCallback } from 'react';
import { View, Text, Pressable } from 'react-native';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { saveTrainingModule } from '@/data/training';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import { StyleSheet } from 'react-native-unistyles';

import CollapsibleClassCard from '@/components/Training/CollapsibleClassCard';

const EMPTY_DRAFT = {};

// Wrapper to properly capture index in closures
const ClassCardWrapper = React.memo(({ cls, index, isExpanded, toggleClassExpand, updateClass, removeClass, addClassRequirement, updateClassRequirement, removeClassRequirement }) => {
    const handleToggle = useCallback(() => toggleClassExpand(index), [index, toggleClassExpand]);
    const handleUpdate = useCallback((field, value) => updateClass(index, field, value), [index, updateClass]);
    const handleRemove = useCallback(() => removeClass(index), [index, removeClass]);
    const handleAddReq = useCallback(() => addClassRequirement(index), [index, addClassRequirement]);
    const handleUpdateReq = useCallback((reqIndex, field, value) => updateClassRequirement(index, reqIndex, field, value), [index, updateClassRequirement]);
    const handleRemoveReq = useCallback((reqIndex) => removeClassRequirement(index, reqIndex), [index, removeClassRequirement]);

    return (
        <CollapsibleClassCard
            cls={cls}
            index={index}
            isExpanded={isExpanded}
            onToggle={handleToggle}
            onUpdate={handleUpdate}
            onRemove={handleRemove}
            onAddRequirement={handleAddReq}
            onUpdateRequirement={handleUpdateReq}
            onRemoveRequirement={handleRemoveReq}
        />
    );
});
export default function CreateTrainingModule() {
    const { id } = useLocalSearchParams();
    const storeKey = id || "new";

    const queryClient = useQueryClient();
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const router = useRouter();

    const storeSnapshot = useEditScreenStore(s => s.CreateTrainingModule?.[storeKey]) || EMPTY_DRAFT;
    const [form, setForm] = useState(() => ({
        ...storeSnapshot,
        Teachers: storeSnapshot.Teachers || (storeSnapshot.Teacher ? [storeSnapshot.Teacher] : []),
        Classes: storeSnapshot.Classes || [],
    }));
    const formRef = useRef(form);
    formRef.current = form;

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: existingItem } = Query.trainingModules.byId({ id, options: { enabled: !!id } });
    const { data: allModulesData } = Query.trainingModules.all({});
    const allModules = allModulesData?.pages?.flatMap(p => p.data)?.filter(m => m.id !== id) || [];

    const saveMutation = useMutation({
        mutationFn: saveTrainingModule,
        onSuccess: (data) => {
            removeEditScreenData("CreateTrainingModule", storeKey);
            queryClient.invalidateQueries({ queryKey: ['trainingModules'] });

            if (data?.id) {
                router.dismissTo({ pathname: '/training/' + data.id });
            } else {
                redirectToDefault(router, "CreateTrainingModule");
            }
        }
    });

    // Sync to store on unmount so data persists across navigations
    useEffect(() => {
        return () => {
            setEditScreenData("CreateTrainingModule", storeKey, formRef.current);
        };
    }, []);

    // Initialize defaults for new module
    useEffect(() => {
        if (!existingItem && !form.name && loggedInUser) {
            setForm(prev => ({
                ...prev,
                Teachers: [loggedInUser],
                status: prev.status || 'ACTIVE',
            }));
        }
    }, [loggedInUser]);

    // Load existing module data
    useEffect(() => {
        if (existingItem) {
            setForm({
                ...existingItem,
                Teachers: existingItem.Teachers || (existingItem.Teacher ? [existingItem.Teacher] : []),
                Classes: (existingItem.Classes || []).map(cls => ({
                    ...cls,
                    Requirements: cls.Requirements || [],
                })),
                originalValues: existingItem,
            });
        }
    }, [existingItem]);

    const updateForm = useCallback((updates) => {
        setForm(prev => ({ ...prev, ...updates }));
    }, []);

    // ── Teachers ──
    const addTeacher = useCallback((person) => {
        if (!person) return;
        setForm(prev => {
            if (prev.Teachers.some(t => t.id === person.id)) return prev;
            return { ...prev, Teachers: [...prev.Teachers, person] };
        });
    }, []);

    const removeTeacher = useCallback((personId) => {
        setForm(prev => ({ ...prev, Teachers: prev.Teachers.filter(t => t.id !== personId) }));
    }, []);

    // ── Classes ──
    const [expandedClassIndex, setExpandedClassIndex] = useState(null);

    const addClass = useCallback(() => {
        const newClass = { name: '', description: '', tempId: Date.now(), Requirements: [] };
        setForm(prev => {
            const newClasses = [...prev.Classes, newClass];
            // Auto-expand the newly added class (index = newClasses.length - 1)
            setExpandedClassIndex(newClasses.length - 1);
            return { ...prev, Classes: newClasses };
        });
    }, []);

    const toggleClassExpand = useCallback((index) => {
        setExpandedClassIndex(prev => prev === index ? null : index);
    }, []);

    const updateClass = useCallback((index, field, value) => {
        setForm(prev => {
            const classes = [...prev.Classes];
            classes[index] = { ...classes[index], [field]: value };
            return { ...prev, Classes: classes };
        });
    }, []);

    const removeClass = useCallback((index) => {
        setForm(prev => {
            const classes = [...prev.Classes];
            classes.splice(index, 1);
            // Adjust expanded index if needed
            if (expandedClassIndex === index) {
                setExpandedClassIndex(null);
            } else if (expandedClassIndex !== null && expandedClassIndex > index) {
                setExpandedClassIndex(expandedClassIndex - 1);
            }
            return { ...prev, Classes: classes };
        });
    }, [expandedClassIndex]);

    // ── Class-level Requirements ──
    const addClassRequirement = useCallback((classIndex) => {
        setForm(prev => {
            const classes = [...prev.Classes];
            const cls = { ...classes[classIndex] };
            cls.Requirements = [...(cls.Requirements || []), { name: '', description: '', tempId: Date.now() }];
            classes[classIndex] = cls;
            return { ...prev, Classes: classes };
        });
    }, []);

    const updateClassRequirement = useCallback((classIndex, reqIndex, field, value) => {
        setForm(prev => {
            const classes = [...prev.Classes];
            const cls = { ...classes[classIndex] };
            const reqs = [...(cls.Requirements || [])];
            reqs[reqIndex] = { ...reqs[reqIndex], [field]: value };
            cls.Requirements = reqs;
            classes[classIndex] = cls;
            return { ...prev, Classes: classes };
        });
    }, []);

    const removeClassRequirement = useCallback((classIndex, reqIndex) => {
        setForm(prev => {
            const classes = [...prev.Classes];
            const cls = { ...classes[classIndex] };
            const reqs = [...(cls.Requirements || [])];
            reqs.splice(reqIndex, 1);
            cls.Requirements = reqs;
            classes[classIndex] = cls;
            return { ...prev, Classes: classes };
        });
    }, []);

    return (
        <ModalWrapper
            loading={saveMutation.isPending}
            onConfirm={() => {
                if (!form.name) {
                    Toast.show({ type: 'error', text2: 'Please enter a module name' });
                    return;
                }

                const validClasses = form.Classes.filter(c => c.name?.trim()).map(c => ({
                    ...c,
                    Requirements: (c.Requirements || []).filter(r => r.name?.trim()),
                }));

                const { originalValues, ...cleanForm } = form;
                saveMutation.mutate({
                    ...cleanForm,
                    Teachers: cleanForm.Teachers,
                    teacherID: cleanForm.Teachers?.[0]?.id || null,
                    Classes: validClasses,
                    originalValues,
                });
            }}
            title={id ? "Update Training Module" : "New Training Module"}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                <InputSection title="Module Details" description="Set up the basic information for this training module.">
                    <Input
                        required
                        title="Module Name"
                        placeholder="e.g. Beginnings"
                        maxLength={120}
                        value={form.name}
                        onUpdate={(v) => updateForm({ name: v })}
                    />
                    <Input
                        title="Description"
                        multiline
                        placeholder="Describe what this module covers..."
                        value={form.description}
                        onUpdate={(v) => updateForm({ description: v })}
                    />
                    <SelectInput
                        title="Status"
                        value={form.status}
                        options={[
                            {value:"ACTIVE", label:"Active"}, 
                            {value:"DRAFT", label:"Draft"}, 
                            {value:"ARCHIVED", label:"Archived"}
                        ]}
                        onUpdate={(v) => updateForm({ status: v })}
                    />
                </InputSection>

                <InputSection title="Teachers" description="Select one or more teachers for this training module.">
                    {form.Teachers.length > 0 && (
                        <View style={styles.teacherList}>
                            {form.Teachers.map(teacher => (
                                <View key={teacher.id} style={styles.teacherChip}>
                                    <Avatar profile={teacher} size={24} disableBorder />
                                    <Text style={styles.teacherChipName}>{teacher.firstName} {teacher.lastName}</Text>
                                    <Pressable onPress={() => removeTeacher(teacher.id)} hitSlop={8}>
                                        <Unicon name="close-circle-outline" size={16} colorVariant="textSecondary" />
                                    </Pressable>
                                </View>
                            ))}
                        </View>
                    )}
                    <SheetSelector
                        type="people"
                        title="Add Teacher"
                        fromScreen="training/create"
                        fromStoreKey={storeKey}
                        value={null}
                        onChange={addTeacher}
                    />
                </InputSection>

                <InputSection title="Prerequisite (optional)" description="If set, only people who have completed the prerequisite module can be enrolled.">
                    <SelectInput
                        title="Prerequisite Module"
                        value={form.PrerequisiteModule?.name || allModules.find(m => m.id === form.prerequisiteModuleID)?.name || "None"}
                        options={["None", ...allModules.map(m => m.name)]}
                        onUpdate={(v) => {
                            if (v === "None") {
                                updateForm({ prerequisiteModuleID: null, PrerequisiteModule: null, prerequisiteModuleName: null });
                            } else {
                                const selected = allModules.find(m => m.name === v);
                                updateForm({ prerequisiteModuleID: selected?.id || null, PrerequisiteModule: selected || null, prerequisiteModuleName: v });
                            }
                        }}
                    />
                </InputSection>

                <InputSection title={`Classes (${form.Classes.length})`} description="Add classes/lessons and their requirements. Click a class to edit it.">
                    {form.Classes.map((cls, i) => (
                        <ClassCardWrapper
                            key={cls.id || cls.tempId || i}
                            cls={cls}
                            index={i}
                            isExpanded={expandedClassIndex === i}
                            toggleClassExpand={toggleClassExpand}
                            updateClass={updateClass}
                            removeClass={removeClass}
                            addClassRequirement={addClassRequirement}
                            updateClassRequirement={updateClassRequirement}
                            removeClassRequirement={removeClassRequirement}
                        />
                    ))}
                    <Pressable testID="create-module-add-class-btn" onPress={addClass} style={styles.addButton}>
                        <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                        <Text style={styles.addButtonText}>Add Class</Text>
                    </Pressable>
                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.primary,
        borderStyle: 'dashed',
        justifyContent: 'center',
        marginTop: spacing.sm,
    },
    addButtonText: {
        fontSize: typography.body,
        color: colors.primary,
        fontFamily: fonts.medium,
    },
    teacherList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
        marginBottom: spacing.sm,
    },
    teacherChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        backgroundColor: colors.surface,
        borderRadius: 20,
        paddingVertical: 4,
        paddingLeft: 4,
        paddingRight: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    teacherChipName: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.text,
    },
}));
